import type { MetaDescriptor } from 'react-router';
import { redirect } from 'react-router';
import z from 'zod';

import { invariant } from 'ts-invariant';

import { Duration } from '@boc/duration';
import { genHrefLangs, mergeProductFilters } from '@boc/framework';
import { generateCacheControlV2 } from '@boc/framework/headers/cache';
import { getAnonClient } from '@boc/framework/client';
import { generateCacheKey } from '@boc/framework/request';
import {
  CategoryBySlugDocument,
  InputMaybe,
  ProductFilterInput,
} from '@boc/framework/saleor-graphql';

import type { Route } from './+types/search-by-category';

import { canonicalize, parseParams, urls } from '~/urls';
import { getCache } from '~/framework.server/cache';
import { SALEOR } from '~/framework.server/saleor';

import { ProductResults } from '~/local/products/search/page';

import { brandInfo } from '#/brand';
import { DEFAULT_CHANNEL, channels } from '#/channels';
import { DEFAULT_LOCALE, supportedLocales } from '#/locales';
import { SEARCH_CATEGORIES } from '#/products/search';

import { loader as allLoader, headers as loaderHeaders } from '../search/loader';

export { loaderHeaders as headers };

export const loader = async (
  { params, ...options }: Route.LoaderArgs,
  forceFilter?: InputMaybe<ProductFilterInput>
) => {
  const schemaParams = z.object({
    categorySlug: z.enum(SEARCH_CATEGORIES as [string, ...string[]]),
  });

  const paramsParse = schemaParams.safeParse(params);

  if (!paramsParse.success) {
    // 'bestsellers' is a collection, but old links pointed here as a category
    if (params.categorySlug === 'bestsellers') {
      throw redirect(urls.products.collections(params, { slug: 'bestsellers' }), 301);
    }

    throw new Response('Not Found', {
      status: 404,
      statusText: 'Category not found',
    });
  }

  const { categorySlug } = paramsParse.data;

  const client = getAnonClient(SALEOR);
  const { channel, saleorLocale } = parseParams(params);
  const cache = getCache();

  const response = await cache.getOrSet(
    generateCacheKey(CategoryBySlugDocument, {
      slug: categorySlug,
      locale: saleorLocale,
    }),
    async () => {
      return client.request(CategoryBySlugDocument, {
        slug: categorySlug,
        locale: saleorLocale,
      });
    },
    Duration.days(1)
  );

  if (!response.category?.id) {
    throw new Response('Not Found', {
      status: 404,
      statusText: 'Category not found',
    });
  }

  const filterCondition: ProductFilterInput = {
    categories: [response.category.id],
  };

  const finalFilter = mergeProductFilters(filterCondition, forceFilter || {});

  const loaderData = await allLoader({ params, ...options }, finalFilter, categorySlug);

  return {
    ...loaderData,
    category: response.category,
  };
};

export const meta: Route.MetaFunction = ({ data, params, location }) => {
  const { channel } = parseParams(params);

  const category = data?.category;

  invariant(category, 'Category not loaded for meta on category by slug page');

  const canonical: MetaDescriptor = {
    tagName: 'link',
    rel: 'canonical',
    href: canonicalize(location),
  };

  const hreflangs = genHrefLangs({
    location,
    canonicalize,
    url: (params) =>
      urls.products.categories(params, {
        slug: category.slug,
      }),
    channels: Object.values(channels),
    defaultChannel: DEFAULT_CHANNEL,
    defaultLocale: DEFAULT_LOCALE,
    supportedLocales,
  });

  let title = category.seoTitle;

  if (!title) {
    title = channel.seo?.title ? channel.seo.title(category.name) : category.name;
    title = `Buy ${title} only at ${brandInfo.name}`;
  }

  let description = category.seoDescription;

  if (!description) {
    description = category.description;
  }
  if (!description) {
    description = `${category.name} only at ${brandInfo.name}`;
  }

  description = channel.seo?.description ? channel.seo.description(description) : description;

  return [
    // { name: 'og:image', content: data.product.thumbnail?.url },
    canonical,
    ...hreflangs,
    {
      title,
    },
    {
      name: 'og:title',
      content: title,
    },

    {
      name: 'description',
      content: description,
    },
    {
      name: 'og:description',
      content: description,
    },
  ];
};

export default ProductResults;
